import React from 'react';
import {
  FiEye,
  FiEdit3,
  FiLink,
  FiMail,
  FiUserMinus,
  FiUsers,
  FiTrash2,
} from 'react-icons/fi';

// The approval card body for SharePoint writes: what will happen, in words a
// person recognises (site, folder and file names), never Graph ids. Names of
// places come from the member's connection (placeNames); file and folder
// names come from the call and are checked by the backend against the real
// items, so the card can't show one file while the call changes another.
//
// The document/page body, check-in comment and sharing message stay editable
// in the card's field list (see SHAREPOINT_EDITABLE_KEYS), so this body leaves
// them out.

export const SHAREPOINT_EDITABLE_KEYS = new Set([
  'content_html',
  'comment',
  'message',
]);

const Row = ({ label, children }) => (
  <div className="flex gap-3 py-1 text-sm">
    <span className="w-24 shrink-0 text-[11px] font-medium uppercase leading-5 tracking-wide text-zinc-500">
      {label}
    </span>
    <span className="min-w-0 flex-1 break-words font-medium text-zinc-200">
      {children}
    </span>
  </div>
);

const RoleChip = ({ role }) => {
  const edit = role === 'edit';
  const Icon = edit ? FiEdit3 : FiEye;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
        edit ? 'bg-amber-900/30 text-amber-300' : 'bg-blue-900/30 text-blue-300'
      }`}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      {edit ? 'Can edit' : 'Can view'}
    </span>
  );
};

const placeLabel = (placeNames, placeId) =>
  (placeId && placeNames?.[placeId]) || 'A SharePoint site you turned on';

// folder_id empty means the top level of the place.
const folderLabel = (folderId, folderName) =>
  !folderId ? 'Top level' : folderName || 'A folder in this place';

const fileLabel = (args) => args.file_name || 'The file chosen in chat';

const Table = ({ rows }) => (
  <div className="mt-1 max-h-48 overflow-auto rounded-md border border-zinc-700/60">
    <table className="w-full text-xs text-zinc-300">
      <tbody>
        {rows.slice(0, 20).map((row, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <tr key={i} className="border-b border-zinc-800 last:border-0">
            {(Array.isArray(row) ? row : [row]).map((cell, j) => (
              // eslint-disable-next-line react/no-array-index-key
              <td
                key={j}
                className={`px-2 py-1 ${i === 0 ? 'font-semibold' : ''}`}
              >
                {cell === null || cell === undefined ? '' : String(cell)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
    {rows.length > 20 && (
      <p className="px-2 py-1 text-[11px] text-zinc-500">
        …and {rows.length - 20} more row(s)
      </p>
    )}
  </div>
);

const Slides = ({ slides }) => (
  <ol className="mt-1 list-decimal space-y-1 pl-5 text-sm text-zinc-300">
    {slides.slice(0, 20).map((slide, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <li key={i}>
        <span className="font-medium text-zinc-200">
          {slide?.title || 'Untitled'}
        </span>
        {Array.isArray(slide?.bullets) && slide.bullets.length > 0 && (
          <ul className="mt-0.5 list-disc pl-5 text-xs text-zinc-400">
            {slide.bullets.slice(0, 8).map((b, j) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={j}>{String(b)}</li>
            ))}
          </ul>
        )}
      </li>
    ))}
  </ol>
);

const SharingBody = ({ action, args, placeNames }) => {
  if (action === 'share_with_people') {
    const people = Array.isArray(args.people) ? args.people : [];
    return (
      <>
        <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-zinc-100">
          <FiUsers aria-hidden="true" /> Share with {people.length}{' '}
          {people.length === 1 ? 'person' : 'people'}
        </div>
        <Row label="File">{fileLabel(args)}</Row>
        <Row label="Where">{placeLabel(placeNames, args.place_id)}</Row>
        <Row label="People">
          <ul className="space-y-1">
            {people.map((email) => (
              <li
                key={email}
                className="rounded-md bg-zinc-800 px-2 py-1 text-xs"
              >
                {email}
              </li>
            ))}
          </ul>
        </Row>
        <Row label="Access">
          <RoleChip role={args.role} />
        </Row>
        <Row label="Email them">
          <span className="inline-flex items-center gap-1">
            <FiMail aria-hidden="true" />
            {args.notify === false ? 'No' : 'Yes'}
          </span>
        </Row>
        <p className="mt-1 text-xs text-zinc-500">
          Only people inside your organisation can be added.
        </p>
      </>
    );
  }
  if (action === 'create_link') {
    return (
      <>
        <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-zinc-100">
          <FiLink aria-hidden="true" /> Create a sharing link
        </div>
        <Row label="File">{fileLabel(args)}</Row>
        <Row label="Where">{placeLabel(placeNames, args.place_id)}</Row>
        <Row label="Access">
          <RoleChip role={args.role} />
        </Row>
        <Row label="Works for">People in your organisation only</Row>
      </>
    );
  }
  return (
    <>
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-red-400">
        <FiUserMinus aria-hidden="true" /> Stop sharing
      </div>
      <Row label="File">{fileLabel(args)}</Row>
      <Row label="Where">{placeLabel(placeNames, args.place_id)}</Row>
      <Row label="Loses access">
        {args.access_label || 'The person or link chosen in chat'}
      </Row>
      <p className="mt-1 text-xs text-zinc-500">
        The owner&apos;s access and access from the site stay.
      </p>
    </>
  );
};

const TITLES = {
  create_folder: (a) => `New folder "${a.name}"`,
  create_word: (a) => `New Word document "${a.name}"`,
  create_excel: (a) => `New Excel workbook "${a.name}"`,
  create_powerpoint: (a) => `New PowerPoint "${a.name}"`,
  create_page: (a) => `New OneNote page "${a.title}"`,
  update_word: () => 'Replace the document content (saved as a new version)',
  rename: (a) => `Rename to "${a.name}"`,
  move: () => 'Move to another folder',
  copy: (a) => (a.name ? `Copy as "${a.name}"` : 'Make a copy'),
  delete: () => 'Delete',
  restore_version: (a) => `Restore version ${a.version_id}`,
  update_range: (a) => `Write cells ${a.address} on sheet "${a.sheet}"`,
  add_sheet: (a) => `Add sheet "${a.sheet}"`,
  add_table_rows: (a) =>
    `Add ${Array.isArray(a.values) ? a.values.length : 0} row(s) to table "${a.table}"`,
};

const SharePointApprovalCard = ({ toolName, action, args, placeNames }) => {
  if (toolName === 'sharepoint_sharing') {
    return <SharingBody action={action} args={args} placeNames={placeNames} />;
  }
  const title = TITLES[action];
  const where = placeLabel(placeNames, args.place_id);
  const isCreate = action?.startsWith('create_') && action !== 'create_page';
  const rows = Array.isArray(args.rows) ? args.rows : args.values;

  return (
    <div className="space-y-0.5">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-zinc-100">
        {action === 'delete' && (
          <FiTrash2 className="text-red-400" aria-hidden="true" />
        )}
        {title ? title(args) : action}
      </div>

      {toolName === 'sharepoint_onenote' ? (
        <>
          <Row label="Where">{where}</Row>
          <Row label="Section">
            {args.section_name || 'The section chosen in chat'}
          </Row>
        </>
      ) : isCreate ? (
        <>
          <Row label="Where">{where}</Row>
          <Row label="Folder">
            {folderLabel(args.folder_id, args.folder_name)}
          </Row>
        </>
      ) : (
        <>
          <Row label="File">{fileLabel(args)}</Row>
          <Row label="Where">{where}</Row>
          {(action === 'move' || action === 'copy') && (
            <>
              {action === 'copy' &&
                args.target_place_id &&
                args.target_place_id !== args.place_id && (
                  <Row label="Copy to">
                    {placeLabel(placeNames, args.target_place_id)}
                  </Row>
                )}
              <Row label="Into folder">
                {folderLabel(args.target_folder_id, args.target_folder_name)}
              </Row>
            </>
          )}
          {toolName === 'sharepoint_excel' && args.table && (
            <Row label="Table">{args.table}</Row>
          )}
        </>
      )}

      {Array.isArray(args.slides) && args.slides.length > 0 && (
        <Row label="Slides">
          <Slides slides={args.slides} />
        </Row>
      )}
      {Array.isArray(rows) && rows.length > 0 && (
        <Row label={action === 'create_excel' ? 'Rows' : 'Values'}>
          <Table rows={rows} />
        </Row>
      )}

      {action === 'delete' && (
        <p className="mt-2 rounded-lg border border-amber-800/50 bg-amber-900/20 px-3 py-2 text-xs font-medium text-amber-400">
          It moves to the SharePoint recycle bin and can be restored there.
        </p>
      )}
      {action === 'restore_version' && (
        <p className="mt-1 text-xs text-zinc-500">
          That version becomes the newest; later versions stay in the history.
        </p>
      )}
    </div>
  );
};

export default SharePointApprovalCard;
